/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '../../assets/close.svg';
import { TiLocation } from 'react-icons/ti';
import { FaHouse, FaLocationDot, FaAngleLeft } from 'react-icons/fa6';
import { PiBagSimpleFill } from 'react-icons/pi';
import SpinnerIcon from '../../assets/spinner3.svg';
import { MapContainer, TileLayer, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import LocateIcon from '../../assets/locate-icon.svg';
import markerIcon from '../../assets/map-marker-icon.svg';
import { useUser } from '../../contexts/user';
import { useCart } from '../../contexts/cart';
import { useNavigate } from 'react-router-dom';
import log from '../../utils/logger';
import { getApiErrorMessage } from '../../utils/apiError';
import api from '../../api/api';
import useToast from '../../hooks/useToast';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import Toast from '../Toast';
import { createPortal } from 'react-dom';
import { DEFAULT_CENTER, GEOAPIFY_COUNTRY_CODE, GEOAPIFY_RESULT_LIMIT } from '../../config/appConfig';
 
const GEOAPIFY_KEY = process.env.REACT_APP_GEOAPIFY_KEY;

const AddNewAddressModal = ({ isAddNewAddressModalOpen, onClose, initialAddress, onAddressUpdated, isEdit }) => {
  const { t } = useTranslation();
  useBodyScrollLock(isAddNewAddressModalOpen);
  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedOption, setSelectedOption] = useState('office');
  const [showChangeLocation, setShowChangeLocation] = useState(false);
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [center, setCenter] = useState([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng]);
  const [mapKey, setMapKey] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [addressNumber, setAddressNumber] = useState('');
  const [addressNumberError, setAddressNumberError] = useState('');
  const [addressFetchError, setAddressFetchError] = useState('');
  const [landmark, setLandmark] = useState('');
  const [zone, setZone] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [contactName, setContactName] = useState('');
  const [isFetchingAddress, setIsFetchingAddress] = useState(false);
  const [isFetchingSearch, setIsFetchingSearch] = useState(false);
  const { user, idToken, updateProfile, logout, adminImpersonate } = useUser();
  const { clearCart } = useCart();
  const navigate = useNavigate();
  const { toastMessage, toastType, showToast } = useToast();

  useEffect(() => {
    if (isAddNewAddressModalOpen) {
      setIsAnimating(true);
    } else if (isAnimating) {
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isAddNewAddressModalOpen, isAnimating]);

  useEffect(() => {
    const loadInitialAddress = async () => {
      if (isEdit && initialAddress) {
        const {
          latitude,
          longitude,
          formattedAddress,
          city,
          addressNumber,
          landmark,
          zone,
          phoneNumber,
          contactName,
          addressType,
        } = initialAddress;

        setCenter([latitude, longitude]);
        setAddress(formattedAddress);
        setCity(city);
        setAddressNumber(addressNumber || '');
        setLandmark(landmark || '');
        setZone(zone || '');
        setPhoneNumber(phoneNumber || '');
        setContactName(contactName || '');
        setSelectedOption(addressType || '');
        setMapKey(k => k + 1); // force re-render of the map
      } else {
        try {
          setIsFetchingAddress(true);
          const res = await api.get('/buyer/map/geocode/reverse', {
            params: {
              lat: DEFAULT_CENTER.lat,
              lon: DEFAULT_CENTER.lng,
            },
          });
          const data = res.data.response.data;
          const addr = data.address;

          const street = [addr.house_number, addr.road].filter(Boolean).join(' ');
          const city = addr.city || addr.town || addr.village || '';
          const province = addr.state || '';
          const postal = addr.postcode || '';
          const country = addr.country || '';
          const shortProvince = addr.state_code || province.match(/\b[A-Z]{2}\b/)?.[0] || '';
          const formatted = `${street}, ${city}, ${shortProvince} ${postal}, ${country}`;

          setCenter([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng]);
          setAddress(formatted);
          setCity(city);
          setMapKey(k => k + 1);
          setAddressFetchError('');
        } catch (error) {
          log.error('Failed to load default address:', error);
          setAddressFetchError(t('fetchAddressError'));
        } finally {
          setIsFetchingAddress(false);
        }
      }
    };

    if (isAddNewAddressModalOpen) {
      loadInitialAddress();
    }
  }, [isAddNewAddressModalOpen, initialAddress, isEdit]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchSearchResults(searchQuery);
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  const handleClick = (option) => {
    setSelectedOption(option);
  };

  const handleChangeLocationClick = () => {
    setShowChangeLocation(true);
  };

  const handleBackClick = () => {
    setShowChangeLocation(false);
  };

  const FixedCenterMarker = () => (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -100%)',
        pointerEvents: 'none',
        zIndex: 10,
      }}
    >
      <img src={markerIcon} alt="center marker" width={30} height={40} />
    </div>
  );

  const MapInteraction = ({ onAddressUpdate }) => {
    const map = useMapEvents({
      dragend: async () => {
        const center = map.getCenter();
        try {
          setIsFetchingAddress(true);
          setCenter([center.lat, center.lng]);
          const res = await api.get('/buyer/map/geocode/reverse', {
            params: {
              lat: center.lat,
              lon: center.lng,
            },
          });
          const data = res.data.response.data;

          const addr = data.address;

          const street = [addr.house_number, addr.road].filter(Boolean).join(' ');
          const city = addr.city || addr.town || addr.village || '';
          const province = addr.state || '';
          const postal = addr.postcode || '';
          const country = addr.country || '';

          const shortProvince = addr.state_code || province.match(/\b[A-Z]{2}\b/)?.[0] || '';
          const formatted = `${street}, ${city}, ${shortProvince} ${postal}, ${country}`;

          onAddressUpdate(formatted);
          setCity(city);
          setAddressFetchError('');
        } catch (error) {
          log.error('Reverse geocoding failed:', error);
          setAddressFetchError(t('fetchAddressError'));
        } finally {
          setIsFetchingAddress(false);
        }
      },
    });
    return null;
  };

  const goToCurrentLocation = () => {
    if (!navigator.geolocation) {
      showToast(t('geolocationNotSupported'), 'error');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        setCenter([latitude, longitude]);
        setMapKey((k) => k + 1);

        // Reverse geocode for address
        try {
          setIsFetchingAddress(true);
          const res = await api.get('/buyer/map/geocode/reverse', {
            params: {
              lat: latitude,
              lon: longitude,
            },
          });
          const data = res.data.response.data;
          const addr = data.address;

          const street = [addr.house_number, addr.road].filter(Boolean).join(' ');
          const city = addr.city || addr.town || addr.village || '';
          const province = addr.state || '';
          const postal = addr.postcode || '';
          const country = addr.country || '';

          const shortProvince = addr.state_code || province.match(/\b[A-Z]{2}\b/)?.[0] || '';
          const formatted = `${street}, ${city}, ${shortProvince} ${postal}, ${country}`;

          setAddress(formatted);
          setCity(city);
          setAddressFetchError('');
        } catch (error) {
          log.error('Reverse geocoding failed:', error);
          setAddress(t('addressFetchFailed'));
          setAddressFetchError(t('fetchAddressError'));
        } finally {
          setIsFetchingAddress(false);
        }
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          showToast(t('locationPermissionDenied'), 'error');
        } else {
          showToast(t('locationRetrievalFailed'), 'error');
        }
        log.error(error);
      },
    );
  };

  const fetchSearchResults = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setIsFetchingSearch(true);

      const response = await fetch(
        `https://api.geoapify.com/v1/geocode/search?text=${encodeURIComponent(query)}&bias=proximity:${DEFAULT_CENTER.lat},${DEFAULT_CENTER.lng}&filter=countrycode:${GEOAPIFY_COUNTRY_CODE}&lang=fr&limit=${GEOAPIFY_RESULT_LIMIT}&apiKey=${GEOAPIFY_KEY}`,
      );

      const data = await response.json();

      const getPriority = (item) => {
        const city = (item.properties.city || '').toLowerCase();
        const state = (item.properties.state || '').toLowerCase();

        if (city === 'montreal' || city === 'montréal') {return 1;}
        if (state === 'québec' || state === 'quebec') {return 2;}
        return 3;
      };

      const sorted = data.features.sort((a, b) => getPriority(a) - getPriority(b));
      setSearchResults(sorted);

    } catch (error) {
      log.error('Geoapify search failed:', error);
    } finally {
      setIsFetchingSearch(false);
    }
  };

  const handleSubmit = async () => {
    if (!address || !city) {
      setAddressFetchError(t('fetchAddressError'));
      showToast(t('fetchAddressError'), 'error');
      return;
    }

    if (!addressNumber.trim()) {
      setAddressNumberError(t('houseNumberRequired'));
      return;
    }

    if ((!user || !idToken) && !adminImpersonate) {
      showToast(t('userNotAuthenticated'), 'error');
      return;
    }

    if (!selectedOption) {
      showToast(t('addressTypeError'), 'warning');
      return;
    }

    const payload = {
      latitude: center[0],
      longitude: center[1],
      formattedAddress: address,
      addressNumber,
      landmark,
      city,
      zone,
      phoneNumber,
      contactName,
      addressType: selectedOption,
      isEdit,
      ...(isEdit && initialAddress?._id && { _id: initialAddress._id }),
      adminImpersonateToken: adminImpersonate || null,
      buyerEmail: user?.email,
      buyerId: user?.id,
    };

    try {
      const response = await api.put(
        '/user/locations',
        payload,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        },
      );

      const locations = response.data.response.data.locations;
      updateProfile({ locations: locations });

      if (typeof onAddressUpdated === 'function') {
        onAddressUpdated(locations);
      }

      onClose();
    } catch (error) {
      log.error('Error saving address:', error);
      const errors = error.response?.data?.error;

      let message = '';

      if (errors && typeof errors === 'object') {
        const firstKey = Object.keys(errors)[0];
        if (firstKey && Array.isArray(errors[firstKey]) && errors[firstKey].length > 0) {
          message = errors[firstKey][0];
        }
      }

      if (!message) {
        const raw = error.response?.data?.error;
        message = typeof raw === 'string' ? raw : '';
      }

      if (message === 'TOKEN_EXPIRED' || message === 'INVALID_ID_TOKEN') {
        navigate('/home');
        sessionStorage.setItem('sessionExpired', 'true');
        sessionStorage.setItem(
          'toast',
          JSON.stringify({
            message: t('sessionExpiredMessage'),
            type: 'error',
          }),
        );
        logout();
        clearCart();
      } else {
        const safeMessage = typeof message === 'string' && message.trim().length > 0
          ? message.trim()
          : null;
        showToast(safeMessage || getApiErrorMessage(error, t), 'error');
      }
    }
  };

  return (
    createPortal(
      <div>
        <div
          className={`fixed bottom-0 lg:top-0 left-0 w-full lg:w-[550px] h-fit lg:h-full bg-white z-50 transition-transform duration-300 ${isAnimating ? 'modalanimated' : 'whennotmodalclose'}`}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white z-50 flex justify-between items-center p-4 border-b">
            <h2 className='font-medium text-xl text-[#000000de]'>{t('Add New Address')}</h2>
            <button
              className='w-9 h-9 flex items-center justify-center rounded-full transition-colors duration-300 hover:bg-[#e7e6e6bf]'
              onClick={onClose}
            >
              <img src={CloseIcon} alt="CloseIcon" />
            </button>
          </div>

          {/* Main Content */}
          <div>
            <div className='overflow-y-auto h-dvh pb-20 lg:pb-40 boxscrollbar'>
              {/* Map */}
              <div className="relative z-0 w-full h-[60dvh] border-t border-black border-opacity-50 outline-none">
                <MapContainer
                  key={mapKey}
                  center={center}
                  zoom={17}
                  className="w-full h-full z-0"
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="&copy; OpenStreetMap contributors"
                  />
                  <MapInteraction onAddressUpdate={setAddress} />
                </MapContainer>

                {/* Center marker */}
                {!showChangeLocation && <FixedCenterMarker />}

                {/* Locate button */}
                <div className="absolute bottom-6 left-0 right-0 flex justify-center items-center flex-col">
                  <button
                    onClick={goToCurrentLocation}
                    className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg shadow-md border border-gray-300 transition hover:bg-gray-200"
                    title="Go to my location"
                  >
                    <img src={LocateIcon} alt="LocateIcon" className="h-5 w-5" />
                    <span className="text-sm font-medium">{t('homeherinputlocatebtn')}</span>
                  </button>
                </div>
              </div>

              <div className='flex gap-2 px-4 pt-5 border-b border-gray-600'>
                <TiLocation className='text-2xl' />
                <div className='flex-1'>
                  <div className='flex items-center justify-between mb-1'>
                    <h3 className='text-[15px] font-semibold'>{city}</h3>
                    <button
                      className='px-4 text-[#1E88E5] text-sm hover:bg-opacity-50 font-semibold uppercase h-9 flex items-center justify-center transition-all duration-500 hover:bg-[#DAEAFB] rounded'
                      onClick={handleChangeLocationClick}
                    >
                      {t('change')}
                    </button>
                  </div>
                  <p className={`font-medium text-[13px] text-[#6B7280] ${
                    addressFetchError ? 'border-red-500' : 'mb-3'}`}>
                    {isFetchingAddress ? (
                      <img src={SpinnerIcon} alt="Loading..." className="h-6 w-6 animate-spin mx-auto -translate-x-4" />
                    ) :
                      address || t('dragMapMessage')
                    }
                  </p>
                  {addressFetchError && (
                    <p className="text-red-500 text-xs mt-1 mb-3">{addressFetchError}</p>
                  )}
                </div>
              </div>

              <form className='py-5 px-4'>
                <input
                  type="text"
                  value={addressNumber}
                  onChange={(e) => {
                    setAddressNumber(e.target.value);
                    if (e.target.value.trim()) {
                      setAddressNumberError('');
                    }
                  }}
                  placeholder={t('House / Flat no.')}
                  className={`py-2 border-b outline-none text-sm text-black placeholder:text-opacity-60 w-full mt-5 ${
                    addressNumberError ? 'border-red-500' : 'border-gray-600 focus:border-blue-700'
                  }`}
                />
                {addressNumberError && (
                  <p className="text-red-500 text-xs mt-1">{addressNumberError}</p>
                )}
                <input type="text"
                  placeholder={t('Landmark')}
                  value={landmark}
                  onChange={(e) => setLandmark(e.target.value)}
                  className='py-2 focus:border-blue-700 border-b border-gray-600 outline-none text-sm text-black placeholder:text-opacity-60 w-full mt-9'
                />
                <input type="text"
                  placeholder={t('City')}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className='py-2 focus:border-blue-700 border-b border-gray-600 outline-none text-sm text-black placeholder:text-opacity-60 w-full mt-9'
                />
                <input type="text"
                  placeholder={t('Area')}
                  value={zone}
                  onChange={(e) => setZone(e.target.value)}
                  className='py-2 focus:border-blue-700 border-b border-gray-600 outline-none text-sm text-black placeholder:text-opacity-60 w-full mt-9'
                />
                <input type="text"
                  placeholder={t('phone')}
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className='py-2 focus:border-blue-700 border-b border-gray-600 outline-none text-sm text-black placeholder:text-opacity-60 w-full mt-9'
                />
                <input type="text"
                  placeholder={t('name')}
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className='py-2 focus:border-blue-700 border-b border-gray-600 outline-none text-sm text-black placeholder:text-opacity-60 w-full mt-9'
                />
                <ul className='flex items-center justify-between gap-2 mt-5'>
                  <li
                    className={`flex items-center px-4 justify-center gap-[6px] font-semibold text-[13px] rounded-2xl h-9 border cursor-pointer transition-all duration-500 ${selectedOption === 'home'
                      ? 'text-[#0976D2] bg-[#DAEAFB]'
                      : 'text-black bg-white border-gray-400'
                    }`}
                    onClick={() => handleClick('home')}
                  >
                    <FaHouse />
                    {t('Home')}
                  </li>
                  <li
                    className={`flex items-center px-4 justify-center gap-[6px] font-semibold text-[13px] rounded-2xl h-9 border cursor-pointer transition-all duration-500 ${selectedOption === 'office'
                      ? 'text-[#0976D2] bg-[#DAEAFB]'
                      : 'text-black bg-white border-gray-400'
                    }`}
                    onClick={() => handleClick('office')}
                  >
                    <PiBagSimpleFill className='text-lg' />
                    {t('Office')}
                  </li>
                  <li
                    className={`flex items-center px-4 justify-center gap-[6px] font-semibold text-[13px] rounded-2xl h-9 border cursor-pointer transition-all duration-500 ${selectedOption === 'other'
                      ? 'text-[#0976D2] bg-[#DAEAFB]'
                      : 'text-black bg-white border-gray-400'
                    }`}
                    onClick={() => handleClick('other')}
                  >
                    <FaLocationDot />
                    {t('Others')}
                  </li>
                </ul>
              </form>
            </div>

            {/* Proceed Button */}
            <div className='bg-white p-2 fixed bottom-0 w-full lg:w-[550px] left-0 lg:left-auto'>
              {/* <button
                              onClick={handleSubmit}
                              disabled={isProceedDisabled}
                              className={`px-5 h-[52px] rounded-lg w-full text-center font-semibold transition-all duration-300 ${
                                  isProceedDisabled
                                  ? 'bg-gray-400 text-white cursor-not-allowed'
                                  : 'bg-[#0B1223] text-white hover:bg-opacity-90'
                              }`}
                              > */}
              <button
                onClick={handleSubmit}
                className={'px-5 h-[52px] rounded-lg w-full text-center font-semibold transition-all duration-300 bg-[#0B1223] text-white hover:bg-opacity-90'}
              >
                {t('Proceed')}
              </button>
            </div>
          </div>

          {/* Change Location */}
          {showChangeLocation && (
            <div
              className="fixed top-[69px] left-0 w-full bg-white p-4"
              style={{ height: 'calc(100dvh - 69px)', zIndex: 60 }}
            >
              <button
                className='w-9 h-9 bg-[#E5E5E5] rounded-lg flex items-center justify-center'
                onClick={handleBackClick}
              >
                <FaAngleLeft />
              </button>

              <div className='input-shadow rounded-[10px] min-h-14 px-3 flex items-center gap-2 border w-full outline-none mt-4 transition-all duration-200'>
                <input
                  type="text"
                  placeholder={t('homeherinputplaceholder')}
                  className='w-full h-full border-none outline-none flex-1 placeholder:font-light'
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
              
              {isFetchingSearch ? (
                <div className="flex items-center justify-center h-32">
                  <img
                    src={SpinnerIcon}
                    alt="Loading..."
                    className="h-5 w-5 animate-spin"
                  />
                </div>
              ) : (
                <ul className='mt-2 max-h-52 overflow-y-auto'>
                  {searchResults.map((result) => {
                    const props = result.properties;
                    const city = props.city || props.county || props.town || props.village || '';
                    const displayName = props.formatted;
                    const [lon, lat] = result.geometry.coordinates;

                    return (
                      <li
                        key={result.properties.place_id || result.properties.datasource.raw.osm_id || Math.random()}
                        onClick={() => {
                          // Update center and force map rerender
                          setCenter([lat, lon]);
                          setMapKey(k => k + 1);

                          // Build formatted address string
                          const street = [props.housenumber, props.street].filter(Boolean).join(' ');
                          const province = props.state || '';
                          const postal = props.postcode || '';
                          const country = props.country || '';
                          const shortProvince = province.match(/\b[A-Z]{2}\b/)?.[0] || '';

                          const formattedAddress = `${street}, ${city}, ${shortProvince} ${postal}, ${country}`;

                          setAddress(formattedAddress);
                          setCity(city);
                          setShowChangeLocation(false);
                          setSearchQuery('');
                          setSearchResults([]);
                        }}
                        className='cursor-pointer px-3 py-2 hover:bg-gray-200 rounded'
                      >
                        {displayName}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
        <Toast message={toastMessage} type={toastType} />
      </div>,
      document.body)
  );
};

export default AddNewAddressModal;
